// YTRepliesConsolePair — Jul 30, 2026 (Randy pt89c-xxxviii)
//
// Wraps two `YTRepliesConsoleCard`s into ONE container. Both stay mounted at
// all times (CSS-visibility swap) so each channel's internal state — sub-tab,
// filter, search, keyword cursor, dismisses — survives a switch. Only rendered
// on surfaces that show BOTH channels together (currently just the Chats tab).
//
// ─────────────────────────────────────────────────────────────────────────
// Jun 2026 (LINKS_CONSOLE_SPRINT.md · §8) — A DECK, NOT TWO TABS.
//
// The old layout gave each channel a full header: icon, status dot,
// "Console · Channel 1", a status sentence and a SETUP button. Two of those
// stacked, and on mobile Channel 2's header wedged itself between Channel 1
// and Channel 1's own tools — two sections competing for attention while the
// one actually in use got interrupted.
//
// So the inactive console is now a card tucked UNDERNEATH the active one:
// a bare peeking edge, inset and dimmed, rounded on the bottom only. Randy:
// *"there is no need for the sliver to be large enough to see any words such
// as refinery."* So it carries NO text at all — it is pure "something is
// behind this". The meaning and the switching live in a pair of carousel pips
// in the active header.
//
// Desktop gets the same thing, bigger. Randy: *"I probably would like that
// even more than side-by-side tabs on desktop. So you can convert desktop to
// match the same UI look as mobile. It'll just be on larger scale."* The
// side-by-side chooser is gone at every breakpoint.
//
// Behavior contract with Randy (pt89c-xxxviii Q1/Q2/Q3), unchanged:
//   • BOTH channels unentitled → collapses to ONE locked card (delegates to
//     YTRepliesConsoleCard's own paywall UX). No deck, no pips.
//   • CHANNEL 2 unentitled (paid Refinery split, hypothetical future SKU)
//     → its pip wears a lock and will not activate.
//   • Collapse chevron is SHARED — collapses the body as a block. Header,
//     pips and sliver stay visible.
//   • No cross-channel notification badges.

import { useState } from "react";
import { YoutubeLogo, LockKey, Gear } from "@phosphor-icons/react";
import { useEntitlements } from "@/lib/entitlements";
import { SLOT_META } from "@/lib/ytRepliesSample";
import YTRepliesConsoleCard from "@/components/YTRepliesConsoleCard";
import YTRepliesSetupModal from "@/components/YTRepliesSetupModal";

// The half of the YouTube Digital Twin each channel represents. Randy: *"on
// the tab titled YouTube Digital Twin it's basically representing both the
// workbench and the refinery. Channel one is Workbench, channel 2 is Refinery,
// so we were just going to tag those two distinctions."* `SLOT_META` already
// held both names, so nothing new is invented here.
const CHANNELS = [
  { slot: "channel1", label: SLOT_META.channel1.name, side: SLOT_META.channel1.where },
  { slot: "channel2", label: SLOT_META.channel2.name, side: SLOT_META.channel2.where },
];

export default function YTRepliesConsolePair({ siloId, siloSlug }) {
  const { owns } = useEntitlements();
  const isUnlocked = owns("yt_ai_replies");
  // Both channels share the same SKU today (creator_pro_module.py catalog
  // note L127). Kept as separate flags so if Randy later splits Refinery
  // into its own SKU, only these change.
  const entitledBySlot = { channel1: isUnlocked, channel2: isUnlocked };

  const [activeChannel, setActiveChannel] = useState("channel1");
  const [collapsed, setCollapsed] = useState(false);
  // Modal open state, keyed by channelSlot so Setup on Channel 1 opens the
  // Channel 1 flow and vice versa.
  const [setupOpenFor, setSetupOpenFor] = useState(null);
  // Per-channel connection status published by each card via onStatusChange.
  const [statusByChannel, setStatusByChannel] = useState({
    channel1: { connected: false, channelName: null, paused: false },
    channel2: { connected: false, channelName: null, paused: false },
  });
  const handleStatus = (s) => {
    if (!s || !s.channelSlot) return;
    setStatusByChannel((prev) => ({ ...prev, [s.channelSlot]: {
      connected: !!s.connected, channelName: s.channelName || null, paused: !!s.paused,
    }}));
  };

  // Locked state: neither channel has the entitlement → render a single
  // locked card (Channel 1) so the existing paywall UX carries. No deck.
  if (!entitledBySlot.channel1 && !entitledBySlot.channel2) {
    return <YTRepliesConsoleCard siloId={siloId} siloSlug={siloSlug} channelSlot="channel1" />;
  }

  const active = CHANNELS.find((c) => c.slot === activeChannel) || CHANNELS[0];
  const behind = CHANNELS.find((c) => c.slot !== activeChannel) || CHANNELS[1];
  const activeStatus = statusByChannel[active.slot];
  const activeEntitled = entitledBySlot[active.slot];
  const behindEntitled = entitledBySlot[behind.slot];

  const bringForward = (slot) => {
    if (!entitledBySlot[slot]) return;
    setActiveChannel(slot);
  };

  return (
    <div className="mt-5 sm:mt-7 mb-2 relative" data-testid="yt-console-pair">
      {/* THE SLIVER — the card behind, peeking out from the active card's TOP
          edge. No text, by instruction. Inset on both sides and dimmed so it
          reads as a card in a deck rather than a border. Taller on desktop
          because the whole deck is on a larger scale there.

          TOP rather than bottom: the console body runs to several thousand
          pixels, so a sliver under the BOTTOM edge sat far below the fold and
          was never seen (caught on the first render of this component). At the
          top it sits right beside the pips, always in view, and still carries
          no words. */}
      <button
        type="button"
        onClick={() => bringForward(behind.slot)}
        disabled={!behindEntitled}
        data-testid={`yt-console-pair-sliver-${behind.slot}`}
        aria-label={
          behindEntitled
            ? `Bring ${behind.label} · ${behind.side} to the front`
            : `${behind.label} is locked`
        }
        title={
          behindEntitled
            ? `${behind.label} · ${behind.side} is behind this one — tap to swap`
            : `${behind.label} is locked (upgrade required)`
        }
        className={`absolute inset-x-0 top-0 -translate-y-[12px] sm:-translate-y-[16px] mx-3 sm:mx-5 h-[16px] sm:h-[22px] rounded-t-2xl border-x border-t hairline bg-[#F1EFE8] transition-all duration-200 ${
          behindEntitled
            ? "cursor-pointer hover:-translate-y-[16px] sm:hover:-translate-y-[21px] hover:bg-[#E9E6DC]"
            : "opacity-50 cursor-not-allowed"
        }`}
      />

      {/* THE ACTIVE CARD — fully forward, full header, drawn over the sliver. */}
      <div className="relative z-10 border hairline rounded-2xl bg-[#FDFCF8] shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 border-b hairline bg-white/40 px-4 py-3">
          <YoutubeLogo size={14} weight="fill" className="text-[#B54848] shrink-0" />
          {activeEntitled ? (
            <span
              data-testid={`yt-console-pair-status-dot-${active.slot}`}
              className={`w-2 h-2 rounded-full shrink-0 ${activeStatus.connected ? "bg-[#2F6B3A]" : "bg-[#C16E5A]"}`}
            />
          ) : (
            <LockKey size={11} weight="duotone" className="text-[#6B6A68] shrink-0" />
          )}
          <div className="text-left min-w-0 flex-1">
            <div
              data-testid={`yt-console-pair-title-${active.slot}`}
              className="text-[10px] uppercase tracking-[0.24em] text-[#6B6A68] font-medium truncate"
            >
              Console · {active.label} · {active.side}
            </div>
            <div className="text-[12px] font-medium text-[#1C1B1A] flex items-center gap-1.5 truncate">
              {!activeEntitled ? (
                <span className="text-[#6B6A68]">Locked (upgrade required)</span>
              ) : activeStatus.connected ? (
                <>
                  <span className="truncate">Connected as {activeStatus.channelName}</span>
                  {activeStatus.paused && (
                    <span className="text-[9px] px-1 py-0.5 rounded bg-[#FDECEC] text-[#B54848] uppercase tracking-[0.14em] font-medium shrink-0">
                      Paused
                    </span>
                  )}
                </>
              ) : (
                <span className="text-[#6B6A68]">Not connected</span>
              )}
            </div>
          </div>

          {/* THE PIPS — which card of the two is forward, and the way to swap.
              Deliberately the only thing that carries channel meaning, since
              the sliver carries none. */}
          <div
            className="shrink-0 flex items-center gap-1.5 sm:gap-2 px-1"
            role="tablist"
            aria-label="Console channel"
            data-testid="yt-console-pair-pips"
          >
            {CHANNELS.map((c) => {
              const isActive = c.slot === activeChannel;
              const entitled = entitledBySlot[c.slot];
              return (
                <button
                  key={c.slot}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={`${c.label} · ${c.side}`}
                  title={`${c.label} · ${c.side}${entitled ? "" : " (locked)"}`}
                  disabled={!entitled}
                  onClick={() => bringForward(c.slot)}
                  data-testid={`yt-console-pair-pip-${c.slot}`}
                  className={`rounded-full transition-all duration-200 ${
                    isActive
                      ? "w-4 h-1.5 sm:w-5 sm:h-2 bg-[#4A5D4E]"
                      : entitled
                        ? "w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#C9C6BC] hover:bg-[#8A9A8D] cursor-pointer"
                        : "w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#E0DDD4] cursor-not-allowed"
                  }`}
                />
              );
            })}
          </div>

          {activeEntitled && (
            <button
              type="button"
              onClick={() => setSetupOpenFor(active.slot)}
              data-testid={`yt-console-pair-setup-${active.slot}`}
              className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md border hairline bg-white text-[10px] uppercase tracking-[0.16em] text-[#4A5D4E] hover:bg-[#F4F3EE] hover:text-[#1C1B1A] transition"
              title={activeStatus.connected ? `Reconnect or manage ${active.label}` : `Connect ${active.label}`}
            >
              <Gear size={11} weight="duotone" />
              Setup
            </button>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            data-testid="yt-console-pair-collapse"
            className="shrink-0 text-[10px] uppercase tracking-[0.16em] text-[#4A5D4E] hover:text-[#1C1B1A] px-1.5 py-1"
          >
            {collapsed ? "▾" : "▴"}
          </button>
        </div>

        {/* Bodies — BOTH mounted at all times (display:none swap) so per-channel
            state (sub-tab, keyword cursor, filter, dismisses) survives a swap. */}
        {!collapsed && (
          <>
            <div style={{ display: activeChannel === "channel1" ? "block" : "none" }}>
              <YTRepliesConsoleCard
                siloId={siloId}
                siloSlug={siloSlug}
                channelSlot="channel1"
                pairMode
                onStatusChange={handleStatus}
              />
            </div>
            <div style={{ display: activeChannel === "channel2" ? "block" : "none" }}>
              <YTRepliesConsoleCard
                siloId={siloId}
                siloSlug={siloSlug}
                channelSlot="channel2"
                pairMode
                onStatusChange={handleStatus}
              />
            </div>
          </>
        )}
      </div>

      {/* Shared setup modal. `key` on channelSlot forces a remount when Randy
          switches which slot is being set up, so the modal always shows fresh
          state for THAT slot. */}
      {setupOpenFor && (
        <YTRepliesSetupModal
          key={setupOpenFor}
          open={true}
          siloId={siloId}
          channelSlot={setupOpenFor}
          onClose={() => setSetupOpenFor(null)}
        />
      )}
    </div>
  );
}
